<template>
  <sidebar/>

  <main :class="{ busy: page.busy }">
    <header class="mt-10 mb-7.5 mx-12 items-end relative">
      <breadcrumbs/>
      <div class="">
        <title-component />
      </div>
      <div v-if="isAdmin" class="rulesLink">
        <a :href="link" target="_blank">
          Business Rules
        </a>
      </div>
    </header>

    <section class="main-section">
      <slot/>
    </section>

    <div v-if="page.busy" class="spinner">
      <Spinner/>
    </div>
  </main>
</template>

<script lang="ts" setup>
import Sidebar from '@components/structure/Sidebar.vue';
import Breadcrumbs from '@components/structure/navigation/Breadcrumbs.vue';
import TitleComponent from '@components/structure/navigation/Title.vue';
import Spinner from '@components/utils/Spinner.vue';
import {provide, reactive, ref} from 'vue';
import {pageKey} from '@components/utils/busy/injects';
import {useFetchAction} from '@hooks/fetch';
import {useStore} from '@/store';

const page = reactive({
  busy: false,
});

provide(pageKey, page);

const [exec] = useFetchAction();

const link = ref('');
const loadLink = async () => {
  const result = await exec('/rules_link', {
    method: 'GET',
  });
  link.value = result.link;
};

loadLink();

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];
</script>

<style lang="scss" scoped>
main {
  @apply flex flex-col;

  margin-left: var(--sidebar-width);
  width: calc(100% - var(--sidebar-width));

  @include absolutePseudo("before") {
    @apply w-full h-full top-0 left-0;
    @apply bg-background-light;
    z-index: 110;
    opacity: 0;
    transition: opacity var(--speed-fast) ease;
    pointer-events: none;
  }

  &.busy::before {
    opacity: 1;
    pointer-events: all;
  }
}

.spinner {
  @apply flex items-center justify-center top-0;
  @apply border border-element-neutral rounded-8;
  @apply p-10 bg-white fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2;
  margin-left: calc(var(--sidebar-width) / 2);
  z-index: 115;
}

.main-section {
  @apply border-t-2 border-t-element-neutral grow;
  display: grid;
  grid-template-areas:
    "tabs toolbar"
    "main toolbar"
    "bottom toolbar";

  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto 1fr;

  & > :deep(*) {
    grid-area: main;
  }
}

.rulesLink {
  position: absolute;
  right: 0;
  top: 0;
}
</style>
